import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Dialog, IconButton, TextField, Typography } from '@mui/material';
import DosenButton from '../../../../../components/ui/Buttons/DosenButton';
import CloseIcon from '@mui/icons-material/Close';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import DescriptionIcon from '@mui/icons-material/Description';
import { toast, type Id } from 'react-toastify';
import { API } from '../../../../../api';
import { useAIMDContext } from '../context/AIMDContext';
import { useBuilderWritable } from '../../../../../components/program-collaboration';
import StepInviteAdmin from '../components/StepInviteAdmin';
import {
  ContentData,
  GenerationStatus,
  GeneratedModule,
  GeneratedLesson,
  MCQ,
  ModuleStructure,
  ProgramStructure
} from '../types';
import ModuleSidebar from '../components/ModuleSidebar';
import LessonContentView, { type RoleplayPolicy } from '../components/LessonContentView';
import LessonTagLine from '../components/LessonTagLine';
import LessonPurposeDialog, { type LessonPurposeOption } from '../components/LessonPurposeDialog';
import CapabilityConfirm, { toastWithUndo, type CapabilityConfirmRequest } from '../components/CapabilityConfirm';
import {
  lessonCapability,
  savedRoleplayFlags,
  useLessonTags,
  withLessonTag,
  type LessonTagChoice
} from '../lessonTags';
import {
  capabilitiesLeftWithoutLesson,
  capabilityLostByRetag,
  lastLessonWarning,
  lastScoredRoleplayOf,
  lessonsOfModule,
  otherScoredRoleplayLesson,
  scoredRoleplayCounts
} from '../capabilityGuards';
import GeneratingOverlay from '../components/GeneratingOverlay';
import EditLessonModal from '../components/EditLessonModal';
import ImproveWithAIModal from '../components/ImproveWithAIModal';
import { hasLessonVideo } from '../utils/lessonMedia';
import { lessonVideoJobExpired } from '../../../../../utils/lessonVideoJobDeadline';
import AddEditContent from '../../Help/components/AddEditContent';
import DueDateModal, {
  DueDateData
} from '../../Program/components/program tabs/settings components/Dialog/DueDateModal';

interface ContentStepProps {
  /** Called every time any content change is made — parent owns the dirty flag. */
  onMarkDirty?: () => void;
  onSave?: () => Promise<void>;
  /** Called with true when a video generation job starts, false when all jobs complete. */
  onVideoGeneratingChange?: (isGenerating: boolean) => void;
}

const ContentStep: React.FC<ContentStepProps> = ({ onMarkDirty, onSave, onVideoGeneratingChange }) => {
  const ctx = useAIMDContext();
  const {
    state,
    generationPhase,
    generationProgress,
    generationError,
    pollingActive,
    embedded,
    handleGenerateContent,
    handleRegenerateLesson,
    handleRegenerateModule,
    handleRegenerateProgram,
    startContentPolling,
    updateContent,
    updateStructure
  } = ctx;

  const content = state.content.generatedContent as GenerationStatus | null;
  const structure = state.structure.generatedStructure as ProgramStructure | null;
  const modules: GeneratedModule[] = content?.modules || [];
  const structureModules = structure?.modules || [];
  const isContentGenerating = generationPhase === 'content';

  const [selectedModuleIdx, setSelectedModuleIdx] = useState<number | null>(null);
  const [selectedLessonIdx, setSelectedLessonIdx] = useState<number | null>(null);
  const [editLessonOpen, setEditLessonOpen] = useState(false);
  const [mediaImageCount, setMediaImageCount] = useState(0);
  const [mediaVideoCount, setMediaVideoCount] = useState(0);
  const [mediaDialog, setMediaDialog] = useState<{ type: 'image' | 'video'; open: boolean }>({
    type: 'image',
    open: false
  });
  const [mediaPrompt, setMediaPrompt] = useState('');
  const [videoPollingJobs, setVideoPollingJobs] =
    useState<{ videoJobId: string; moduleIdx: number; lessonIdx: number; startedAt: number }[]>();
  const [pendingImageLessons, setPendingImageLessons] = useState<Set<string>>(new Set());
  const [improveLessonOpen, setImproveLessonOpen] = useState(false);
  const [improvingLesson, setImprovingLesson] = useState(false);
  const [improveModuleOpen, setImproveModuleOpen] = useState(false);
  const [improveProgramOpen, setImproveProgramOpen] = useState(false);
  const [addMaterialOpen, setAddMaterialOpen] = useState(false);
  const [addMaterialAction, setAddMaterialAction] = useState<'Add' | 'Edit'>('Add');

  const hasTriggered = useRef(false);
  // Only the admin holding the edit lock may start a generation. Anyone else
  // (a read-only viewer, or this admin before the lock answer is back) may
  // watch progress — polling only reads — but must not kick one off.
  const writable = useBuilderWritable();

  // Auto-start generation or resume polling
  useEffect(() => {
    if (hasTriggered.current) return;

    const elliottPending =
      content?.status === 'completed' && (content.modules || []).some((m) => (m.lessons || []).some((l) => !l.elliott));

    if (content && (content.status !== 'completed' || elliottPending) && !pollingActive) {
      hasTriggered.current = true;
      startContentPolling();
    } else if (!content && !isContentGenerating && writable) {
      hasTriggered.current = true;
      handleGenerateContent();
    }
  }, [content, isContentGenerating, pollingActive, handleGenerateContent, startContentPolling, writable]);

  const selectedLesson: GeneratedLesson | null =
    selectedModuleIdx !== null && selectedLessonIdx !== null
      ? modules[selectedModuleIdx]?.lessons?.[selectedLessonIdx] || null
      : null;

  const handleEditSave = useCallback(
    (title: string, contentText: string) => {
      if (selectedModuleIdx === null || selectedLessonIdx === null) return;
      updateContent((prev: ContentData): ContentData => {
        const gen = prev.generatedContent as GenerationStatus | null;
        if (!gen) return prev;
        const modules = gen.modules.map((m, mi) => {
          if (mi !== selectedModuleIdx) return m;
          const lessons = m.lessons.map((l, li) =>
            li === selectedLessonIdx ? { ...l, title, content: contentText } : l
          );
          return { ...m, lessons };
        });
        return { ...prev, generatedContent: { ...gen, modules } };
      });
      setEditLessonOpen(false);
      onMarkDirty?.();
    },
    [selectedModuleIdx, selectedLessonIdx, updateContent, onMarkDirty]
  );

  const handleMaterialUpdate = useCallback(
    (materialIndex: number, updated: any) => {
      if (selectedModuleIdx === null || selectedLessonIdx === null) return;
      updateContent((prev: ContentData): ContentData => {
        const gen = prev.generatedContent as GenerationStatus | null;
        if (!gen) return prev;
        const modules = gen.modules.map((m, mi) => {
          if (mi !== selectedModuleIdx) return m;
          const lessons = m.lessons.map((l, li) => {
            if (li !== selectedLessonIdx) return l;
            const mats = [...(l.materials || [])];
            mats[materialIndex] = updated;
            return { ...l, materials: mats };
          });
          return { ...m, lessons };
        });
        return { ...prev, generatedContent: { ...gen, modules } };
      });
      onMarkDirty?.();
    },
    [selectedModuleIdx, selectedLessonIdx, updateContent, onMarkDirty]
  );

  const handleRemoveMaterial = useCallback(
    (materialIndex: number) => {
      if (selectedModuleIdx === null || selectedLessonIdx === null) return;
      updateContent((prev: ContentData): ContentData => {
        const gen = prev.generatedContent as GenerationStatus | null;
        if (!gen) return prev;
        const modules = gen.modules.map((m, mi) => {
          if (mi !== selectedModuleIdx) return m;
          const lessons = m.lessons.map((l, li) => {
            if (li !== selectedLessonIdx) return l;
            const mats = (l.materials || []).filter((_, i) => i !== materialIndex);
            return { ...l, materials: mats };
          });
          return { ...m, lessons };
        });
        return { ...prev, generatedContent: { ...gen, modules } };
      });
      onMarkDirty?.();
    },
    [selectedModuleIdx, selectedLessonIdx, updateContent, onMarkDirty]
  );

  const handleAssessmentsUpdate = useCallback(
    (updatedMcqs: MCQ[]) => {
      if (selectedModuleIdx === null || selectedLessonIdx === null) return;
      updateContent((prev: ContentData): ContentData => {
        const gen = prev.generatedContent as GenerationStatus | null;
        if (!gen) return prev;
        const modules = gen.modules.map((m, mi) => {
          if (mi !== selectedModuleIdx) return m;
          const lessons = m.lessons.map((l, li) => (li === selectedLessonIdx ? { ...l, mcqs: updatedMcqs } : l));
          return { ...m, lessons };
        });
        return { ...prev, generatedContent: { ...gen, modules } };
      });
      onMarkDirty?.();
    },
    [selectedModuleIdx, selectedLessonIdx, updateContent, onMarkDirty]
  );

  const handleNewArticleCreated = useCallback(
    (article: any) => {
      if (selectedModuleIdx === null || selectedLessonIdx === null) return;
      const newMaterial = {
        _id: article?._id || article?.id || crypto.randomUUID(),
        title: article?.articleName || article?.title || 'Untitled',
        description: article?.articleDescription || article?.description || '',
        thumbnail: article?.coverImageUrl || article?.coverImg || ''
      };
      updateContent((prev: ContentData): ContentData => {
        const gen = prev.generatedContent as GenerationStatus | null;
        if (!gen) return prev;
        const modules = gen.modules.map((m, mi) => {
          if (mi !== selectedModuleIdx) return m;
          const lessons = m.lessons.map((l, li) => {
            if (li !== selectedLessonIdx) return l;
            return { ...l, materials: [...(l.materials || []), newMaterial] };
          });
          return { ...m, lessons };
        });
        return { ...prev, generatedContent: { ...gen, modules } };
      });
      onMarkDirty?.();
    },
    [selectedModuleIdx, selectedLessonIdx, updateContent, onMarkDirty]
  );

  const handleImproveLessonSubmit = useCallback(
    async (feedback: string) => {
      if (selectedModuleIdx === null || selectedLessonIdx === null) return;
      setImproveLessonOpen(false);
      setImprovingLesson(true);
      try {
        await handleRegenerateLesson(selectedModuleIdx, selectedLessonIdx, feedback);
      } finally {
        setImprovingLesson(false);
      }
      onMarkDirty?.();
    },
    [selectedModuleIdx, selectedLessonIdx, handleRegenerateLesson, onMarkDirty]
  );

  const handleImproveModuleSubmit = useCallback(
    async (feedback: string) => {
      if (selectedModuleIdx === null) return;
      await handleRegenerateModule(selectedModuleIdx, feedback);
      setImproveModuleOpen(false);
      onMarkDirty?.();
    },
    [selectedModuleIdx, handleRegenerateModule, onMarkDirty]
  );

  const handleImproveProgramSubmit = useCallback(
    async (feedback: string) => {
      await handleRegenerateProgram(feedback);
      setImproveProgramOpen(false);
      onMarkDirty?.();
    },
    [handleRegenerateProgram, onMarkDirty]
  );

  const handleRenameModule = useCallback(
    (idx: number, title: string) => {
      updateContent((prev: ContentData): ContentData => {
        const gen = prev.generatedContent as GenerationStatus | null;
        if (!gen?.modules?.[idx]) return prev;
        const updated = gen.modules.map((m, i) => (i === idx ? { ...m, title } : m));
        return { ...prev, generatedContent: { ...gen, modules: updated } };
      });
      if (structure?.modules?.[idx]) {
        const updatedStructure = structure.modules.map((m, i) => (i === idx ? { ...m, title } : m));
        updateStructure({ generatedStructure: { modules: updatedStructure } });
      }
      onMarkDirty?.();
    },
    [updateContent, updateStructure, structure, onMarkDirty]
  );

  const handleRenameLesson = useCallback(
    (moduleIdx: number, lessonIdx: number, title: string) => {
      updateContent((prev: ContentData): ContentData => {
        const gen = prev.generatedContent as GenerationStatus | null;
        if (!gen?.modules?.[moduleIdx]?.lessons?.[lessonIdx]) return prev;
        const updated = gen.modules.map((m, mi) => {
          if (mi !== moduleIdx) return m;
          const lessons = m.lessons.map((l, li) => (li === lessonIdx ? { ...l, title } : l));
          return { ...m, lessons };
        });
        return { ...prev, generatedContent: { ...gen, modules: updated } };
      });
      if (structure?.modules?.[moduleIdx]?.lessons?.[lessonIdx]) {
        const updatedStructure = structure.modules.map((m, mi) => {
          if (mi !== moduleIdx) return m;
          const lessons = m.lessons.map((l, li) => (li === lessonIdx ? { ...l, title } : l));
          return { ...m, lessons };
        });
        updateStructure({ generatedStructure: { modules: updatedStructure } });
      }
      onMarkDirty?.();
    },
    [updateContent, updateStructure, structure, onMarkDirty]
  );

  const handleRemoveMedia = useCallback(
    (src: string, type: 'img' | 'video', modIdx: number, lesIdx: number) => {
      updateContent((prev: ContentData): ContentData => {
        const gen = prev.generatedContent as GenerationStatus | null;
        if (!gen) return prev;
        const modules = gen.modules.map((m, mi) => {
          if (mi !== modIdx) return m;
          const lessons = m.lessons.map((l, li) => {
            if (li !== lesIdx) return l;
            const escapedSrc = src.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            // Remove our wrapper div + the media tag, or the bare tag if no wrapper
            let content = l.content || '';
            content = content
              .replace(
                new RegExp(
                  `<div[^>]*>\\s*<${type}[^>]+src=["']${escapedSrc}["'][^>]*>(?:[\\s\\S]*?</${type}>)?\\s*</div>`,
                  'i'
                ),
                ''
              )
              .replace(new RegExp(`<${type}[^>]+src=["']${escapedSrc}["'][^>]*/?>`, 'i'), '');
            return { ...l, content: content.trim() };
          });
          return { ...m, lessons };
        });
        return { ...prev, generatedContent: { ...gen, modules } };
      });
      onMarkDirty?.();
    },
    [updateContent, onMarkDirty]
  );

  const appendMediaToLesson = useCallback(
    (moduleIdx: number, lessonIdx: number, html: string) => {
      updateContent((prev: ContentData): ContentData => {
        const gen = prev.generatedContent as GenerationStatus | null;
        if (!gen) return prev;
        const modules = gen.modules.map((m, mi) => {
          if (mi !== moduleIdx) return m;
          const lessons = m.lessons.map((l, li) => {
            if (li !== lessonIdx) return l;
            // Always append as a block-level element so media never lands inside a sentence
            const existing = (l.content || '').trimEnd();
            return { ...l, content: existing + `<div style="margin:16px 0 0">${html}</div>` };
          });
          return { ...m, lessons };
        });
        return { ...prev, generatedContent: { ...gen, modules } };
      });
    },
    [updateContent]
  );

  const dropVideoPollingJob = useCallback(
    (videoJobId: string) => setVideoPollingJobs((prev) => prev?.filter((j) => j.videoJobId !== videoJobId)),
    []
  );

  useEffect(() => {
    if (!videoPollingJobs?.length || !ctx.state.generationId) return;
    const genId = ctx.state.generationId;
    const interval = setInterval(async () => {
      for (const job of videoPollingJobs) {
        // Nothing on the server ever ends one of these jobs, so give up here.
        // A job left in the list keeps Save and Publish disabled on this step.
        if (lessonVideoJobExpired(job.startedAt)) {
          toast.error("Your video didn't arrive in time. Try generating it again.");
          dropVideoPollingJob(job.videoJobId);
          continue;
        }
        try {
          const res = await API.getLessonVideoStatus({ programGenerationId: genId, videoJobId: job.videoJobId });
          const data = res?.data ?? res;
          if (data?.status === 'completed' && (data?.videoUrl || data?.url)) {
            appendMediaToLesson(
              job.moduleIdx,
              job.lessonIdx,
              `<video src="${
                data.videoUrl || data.url
              }" controls style="max-width:100%;border-radius:8px;margin:12px 0;" />`
            );
            dropVideoPollingJob(job.videoJobId);
            setMediaVideoCount((c) => c + 1);
            onMarkDirty?.();
          } else if (data?.status === 'failed') {
            toast.error('Elliott could not make the video. Try again.');
            dropVideoPollingJob(job.videoJobId);
          }
        } catch {
          /* keep polling */
        }
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [videoPollingJobs, ctx.state.generationId, appendMediaToLesson, dropVideoPollingJob]);

  useEffect(() => {
    onVideoGeneratingChange?.((videoPollingJobs?.length ?? 0) > 0);
  }, [videoPollingJobs, onVideoGeneratingChange]);

  const runMediaGeneration = async (type: 'image' | 'video', prompt: string, modIdx: number, lesIdx: number) => {
    const genId = ctx.state.generationId;
    if (!genId) return;
    const lessonId = `${modIdx}-${lesIdx}`;
    const lessonKey = `${modIdx}-${lesIdx}`;
    try {
      const programName = ctx.state.requirements?.programName || '';
      const structureModuleList = (ctx.state.structure?.generatedStructure?.modules || modules)
        .map(
          (m: any, mi: number) =>
            `Module ${mi + 1}: ${m.title} (${(m.lessons || []).map((l: any) => l.title).join(', ')})`
        )
        .join('\n');
      const lesson = modules[modIdx]?.lessons?.[lesIdx];

      if (type === 'image') {
        setPendingImageLessons((prev) => new Set([...prev, lessonKey]));
        const res = await API.generateLessonImage({
          programGenerationId: genId,
          lessonId,
          prompt,
          lessonContent: lesson?.content || '',
          programName,
          programContext: structureModuleList
        });
        const data = res?.data ?? res;
        if (data?.imageUrl || data?.url) {
          appendMediaToLesson(
            modIdx,
            lesIdx,
            `<img src="${
              data.imageUrl || data.url
            }" alt="Generated image" style="max-width:100%;border-radius:8px;margin:12px 0;" />`
          );
          setMediaImageCount((c) => c + 1);
          onMarkDirty?.();
          toast.success('Image requested. It will appear here in about 2 minutes.');
        }
      } else {
        const res = await API.generateLessonVideo({
          programGenerationId: genId,
          lessonId,
          prompt,
          lessonContent: lesson?.content || '',
          programName,
          programContext: structureModuleList
        });
        const data = res?.data ?? res;
        if (data?.videoJobId) {
          setVideoPollingJobs((prev) => [
            ...(prev || []),
            { videoJobId: data.videoJobId, moduleIdx: modIdx, lessonIdx: lesIdx, startedAt: Date.now() }
          ]);
          toast.success('Video requested. It will appear here in 5 to 7 minutes.');
        }
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || `Elliott could not make the ${type}. Try again.`);
    } finally {
      setPendingImageLessons((prev) => {
        const s = new Set(prev);
        s.delete(lessonKey);
        return s;
      });
    }
  };

  const handleGenerateMedia = () => {
    const genId = ctx.state.generationId;
    if (!genId || selectedModuleIdx === null || selectedLessonIdx === null || !mediaPrompt.trim()) return;
    const { type } = mediaDialog;
    const prompt = mediaPrompt;
    const modIdx = selectedModuleIdx;
    const lesIdx = selectedLessonIdx;
    // Close modal immediately, then run in background
    setMediaDialog({ type: 'image', open: false });
    setMediaPrompt('');
    runMediaGeneration(type, prompt, modIdx, lesIdx);
  };

  // Sidebar uses structure modules for names, generated modules for status
  const sidebarModules: ModuleStructure[] =
    structureModules.length > 0
      ? structureModules
      : modules.map((m) => ({
          title: m.title,
          reasoning: m.reasoning,
          learningOutcomesServed: m.learningOutcomesServed,
          ragDocumentTitles: m.ragDocumentTitles,
          lessons: m.lessons.map((l) => ({ title: l.title, reasoning: l.reasoning }))
        }));

  const sidebarModulesRef = useRef(sidebarModules);
  sidebarModulesRef.current = sidebarModules;

  const handleReorder = useCallback(
    (reordered: typeof sidebarModules) => {
      updateStructure({ generatedStructure: { modules: reordered } });

      const oldSidebar = sidebarModulesRef.current;

      updateContent((prev: ContentData): ContentData => {
        const gen = prev.generatedContent as GenerationStatus | null;
        if (!gen?.modules || gen.modules.length === 0) return prev;

        const isModuleReorder = reordered.every((m) => oldSidebar.includes(m));

        if (isModuleReorder) {
          const reorderedContent = reordered.map((m) => gen.modules[oldSidebar.indexOf(m)]);
          return { ...prev, generatedContent: { ...gen, modules: reorderedContent } };
        }

        // Lesson reorder: modules stay in same positions, lessons moved between them
        const reorderedContent = reordered.map((newMod, mIdx) => {
          const oldContentMod = gen.modules[mIdx];
          if (!oldContentMod) return oldContentMod;

          const reorderedLessons = newMod.lessons
            .map((lesRef) => {
              for (let origM = 0; origM < oldSidebar.length; origM++) {
                const origL = oldSidebar[origM].lessons.indexOf(lesRef);
                if (origL !== -1) return gen.modules[origM]?.lessons[origL];
              }
              return null;
            })
            .filter(Boolean) as GeneratedLesson[];

          return {
            ...oldContentMod,
            lessons: reorderedLessons,
            totalLessonsCount: reorderedLessons.length,
            completedLessonsCount: reorderedLessons.filter((l) => l.status === 'completed').length
          };
        });

        return { ...prev, generatedContent: { ...gen, modules: reorderedContent } };
      });
      onMarkDirty?.();
    },
    [updateStructure, updateContent, onMarkDirty]
  );

  // ----- Add, delete and what each lesson is for (docs/PRD-capability-lesson-roleplays.md R1, R2, R5, R9) -----

  // The Capability build flow: the program's capabilities and outcomes (null for any other program).
  const lessonTags = useLessonTags();
  // Which lessons have a Scored Roleplay on, as the server keeps them (the content is its status payload).
  const scoredFlags = savedRoleplayFlags(modules);
  const [confirm, setConfirm] = useState<CapabilityConfirmRequest | null>(null);
  const [purpose, setPurpose] = useState<
    | (Omit<React.ComponentProps<typeof LessonPurposeDialog>, 'open' | 'onCancel' | 'onConfirm'> & {
        resolve: (picked: { choice: LessonTagChoice | null; title: string } | null) => void;
      })
    | null
  >(null);
  const askPurpose = (ask: Omit<NonNullable<typeof purpose>, 'resolve'>) =>
    new Promise<{ choice: LessonTagChoice | null; title: string } | null>((resolve) => {
      setPurpose({ ...ask, resolve });
    });
  const closePurpose = (picked: { choice: LessonTagChoice | null; title: string } | null) => {
    purpose?.resolve(picked);
    setPurpose(null);
  };

  const capabilityOptions = (): LessonPurposeOption[] => {
    if (!lessonTags) return [];
    const counts = scoredRoleplayCounts(modules, scoredFlags);
    return lessonTags.capabilities.map((c) => {
      const n = counts[c.id] ?? 0;
      return { choice: { capabilityId: c.id }, label: c.name, note: `${n} Scored Roleplay${n === 1 ? '' : 's'}` };
    });
  };
  const outcomeOptions = (): LessonPurposeOption[] =>
    lessonTags ? lessonTags.outcomes.map((o) => ({ choice: { outcome: o }, label: o })) : [];

  // A delete lives only in the browser until the next save, so Undo restores it exactly. Once the structure is
  // saved the server has it too: the Undo toast goes, rather than offer a restore that would come back without
  // the lesson's Elliott sections.
  const undoToastRef = useRef<Id | null>(null);
  useEffect(() => {
    if (undoToastRef.current === null) return;
    toast.dismiss(undoToastRef.current);
    undoToastRef.current = null;
  }, [ctx.structureSavedAt]);

  const deleteFromCourse = useCallback(
    (mIdx: number, lIdx: number | null) => {
      const beforeStructure = structureModules;
      const beforeContent = content;
      const without = <T,>(list: T[], i: number) => list.filter((_, j) => j !== i);
      updateStructure({
        generatedStructure: {
          modules:
            lIdx === null
              ? without(structureModules, mIdx)
              : structureModules.map((m, i) => (i === mIdx ? { ...m, lessons: without(m.lessons, lIdx) } : m))
        }
      });
      updateContent((prev: ContentData): ContentData => {
        const gen = prev.generatedContent as GenerationStatus | null;
        if (!gen?.modules) return prev;
        const next =
          lIdx === null
            ? without(gen.modules, mIdx)
            : gen.modules.map((m, i) => {
                if (i !== mIdx) return m;
                const lessons = without(m.lessons, lIdx);
                return {
                  ...m,
                  lessons,
                  totalLessonsCount: lessons.length,
                  completedLessonsCount: lessons.filter((l) => l.status === 'completed').length
                };
              });
        return { ...prev, generatedContent: { ...gen, modules: next } };
      });
      setSelectedModuleIdx(null);
      setSelectedLessonIdx(null);
      onMarkDirty?.();
      undoToastRef.current = toastWithUndo(lIdx === null ? 'Module deleted.' : 'Lesson deleted.', () => {
        undoToastRef.current = null;
        updateStructure({ generatedStructure: { modules: beforeStructure } });
        updateContent((prev: ContentData): ContentData => ({ ...prev, generatedContent: beforeContent }));
      });
    },
    [structureModules, content, updateStructure, updateContent, onMarkDirty]
  );

  const handleDeleteLesson = useCallback(
    (mIdx: number, lIdx: number) => {
      if ((structureModules[mIdx]?.lessons.length ?? 0) <= 1) {
        toast.info('A module needs at least one lesson. To remove this one, delete the module.');
        return;
      }
      const lost = lessonTags
        ? capabilitiesLeftWithoutLesson(structureModules, lessonTags, [{ moduleIndex: mIdx, lessonIndex: lIdx }])
        : [];
      if (lost.length === 0) return deleteFromCourse(mIdx, lIdx);
      setConfirm({
        title: 'Delete this lesson?',
        body: lastLessonWarning(lost),
        confirmLabel: 'Delete anyway',
        onConfirm: () => deleteFromCourse(mIdx, lIdx)
      });
    },
    [structureModules, lessonTags, deleteFromCourse]
  );

  const handleDeleteModule = useCallback(
    (mIdx: number) => {
      if (structureModules.length <= 1) {
        toast.info('A course needs at least one module.');
        return;
      }
      const lost = lessonTags
        ? capabilitiesLeftWithoutLesson(structureModules, lessonTags, lessonsOfModule(structureModules, mIdx))
        : [];
      if (lost.length === 0) return deleteFromCourse(mIdx, null);
      setConfirm({
        title: 'Delete this module?',
        body: lastLessonWarning(lost, 'module'),
        confirmLabel: 'Delete anyway',
        onConfirm: () => deleteFromCourse(mIdx, null)
      });
    },
    [structureModules, lessonTags, deleteFromCourse]
  );

  // Add: the admin names the lesson and says what it is for, then Elliott writes it, in view (R1).
  const handleAdd = useCallback(
    async (moduleIndex: number | null) => {
      const isNewModule = moduleIndex === null;
      const mIdx = isNewModule ? structureModules.length : moduleIndex;
      const nextNumber = isNewModule ? 1 : (structureModules[mIdx]?.lessons.length ?? 0) + 1;
      const picked = await askPurpose({
        title: isNewModule ? 'Add a module' : 'Add a lesson',
        body: lessonTags
          ? 'Elliott writes the lesson for what it is for: a capability it builds, or a learning outcome it serves.'
          : 'Elliott writes the lesson from its title.',
        options: [...capabilityOptions(), ...outcomeOptions()],
        titleField: { label: 'Lesson title', initial: `New Lesson ${nextNumber}` },
        confirmLabel: isNewModule ? 'Add module' : 'Add lesson'
      });
      if (!picked) return;
      const choice = picked.choice;
      const tagFields = choice
        ? 'capabilityId' in choice
          ? { capabilityId: choice.capabilityId, outcomes: [] }
          : { capabilityId: null, outcomes: [choice.outcome] }
        : {};
      const lesson = { title: picked.title, reasoning: '', ...tagFields };
      const moduleTitle = `New Module ${structureModules.length + 1}`;
      const nextStructure: ModuleStructure[] = isNewModule
        ? [
            ...structureModules,
            { title: moduleTitle, reasoning: '', learningOutcomesServed: [], ragDocumentTitles: [], lessons: [lesson] }
          ]
        : structureModules.map((m, i) => (i === mIdx ? { ...m, lessons: [...m.lessons, lesson] } : m));
      const lIdx = nextStructure[mIdx].lessons.length - 1;
      const before = structureModules;
      updateStructure({ generatedStructure: { modules: nextStructure } });
      try {
        await ctx.handleSaveStructure(nextStructure);
      } catch (err) {
        updateStructure({ generatedStructure: { modules: before } });
        toast.error((err as Error).message);
        return;
      }
      const newLesson: GeneratedLesson = {
        ...lesson,
        status: 'generating',
        content: '',
        materials: [],
        mcq: null,
        usedRag: false,
        generatedAt: null
      };
      updateContent((prev: ContentData): ContentData => {
        const gen = prev.generatedContent as GenerationStatus | null;
        if (!gen?.modules) return prev;
        const next: GeneratedModule[] = isNewModule
          ? [
              ...gen.modules,
              {
                title: moduleTitle,
                reasoning: '',
                learningOutcomesServed: [],
                ragDocumentTitles: [],
                status: 'generating',
                completedLessonsCount: 0,
                totalLessonsCount: 1,
                lessons: [newLesson]
              }
            ]
          : gen.modules.map((m, i) =>
              i === mIdx ? { ...m, lessons: [...m.lessons, newLesson], totalLessonsCount: m.lessons.length + 1 } : m
            );
        return { ...prev, generatedContent: { ...gen, modules: next } };
      });
      setSelectedModuleIdx(mIdx);
      setSelectedLessonIdx(lIdx);
      const capabilityName =
        choice && 'capabilityId' in choice ? lessonTags?.capabilities.find((c) => c.id === choice.capabilityId)?.name : null;
      const forWhat = capabilityName
        ? ` It builds the capability ${capabilityName}.`
        : choice && 'outcome' in choice
        ? ` It serves the learning outcome "${choice.outcome}".`
        : '';
      await handleRegenerateLesson(mIdx, lIdx, `Write this new lesson from scratch: "${picked.title}".${forWhat}`);
      // Its Elliott sections follow in the background: keep reading until they are there.
      startContentPolling();
      onMarkDirty?.();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [structureModules, lessonTags, ctx, updateStructure, updateContent, handleRegenerateLesson, startContentPolling, onMarkDirty]
  );

  // Retag a lesson to another capability, saved at once (the backend checks every capability keeps a lesson).
  const retagLesson = useCallback(
    async (mIdx: number, lIdx: number, choice: LessonTagChoice): Promise<boolean> => {
      const next = withLessonTag(structureModules, mIdx, lIdx, choice);
      const before = structureModules;
      updateStructure({ generatedStructure: { modules: next } });
      try {
        await ctx.handleSaveStructure(next);
      } catch (err) {
        updateStructure({ generatedStructure: { modules: before } });
        toast.error((err as Error).message);
        return false;
      }
      updateContent((prev: ContentData): ContentData => {
        const gen = prev.generatedContent as GenerationStatus | null;
        if (!gen?.modules) return prev;
        const tagged = withLessonTag(gen.modules, mIdx, lIdx, choice);
        return { ...prev, generatedContent: { ...gen, modules: tagged } };
      });
      onMarkDirty?.();
      return true;
    },
    [structureModules, ctx, updateStructure, updateContent, onMarkDirty]
  );

  // The capability picker behind a Roleplay switched on in an outcome lesson, and behind Change (R9, D1).
  const pickCapability = useCallback(
    async (mIdx: number, lIdx: number, reason: 'switchOn' | 'change'): Promise<{ id: string; name: string } | null> => {
      if (!lessonTags) return null;
      const current = modules[mIdx]?.lessons[lIdx] ?? structureModules[mIdx]?.lessons[lIdx];
      const serves = current?.outcomes?.length ? `"${current.outcomes.join('", "')}"` : 'its outcomes';
      const picked = await askPurpose({
        title: reason === 'switchOn' ? 'Which capability should this Roleplay measure?' : 'Which capability does this lesson build?',
        body:
          reason === 'switchOn'
            ? `Roleplays in this course measure a capability. This lesson will build it instead of serving ${serves}. Its content stays as it is.`
            : 'The lesson keeps its content. Its Roleplay measures the capability you choose.',
        options: capabilityOptions(),
        initial: current?.capabilityId ? { capabilityId: current.capabilityId } : null,
        confirmLabel: reason === 'switchOn' ? 'Add Roleplay' : 'Change'
      });
      const choice = picked?.choice;
      if (!choice || !('capabilityId' in choice)) return null;
      const capability = lessonTags.capabilities.find((c) => c.id === choice.capabilityId) ?? null;
      if (!capability || capability.id === current?.capabilityId) return capability;
      const lost = capabilityLostByRetag(structureModules, lessonTags, { moduleIndex: mIdx, lessonIndex: lIdx }, choice);
      if (lost) {
        // Saved at once here, so the backend would refuse it: say why instead.
        setConfirm({
          title: "This lesson can't change yet",
          body: `This is the last lesson that builds ${lost.name}. Every capability in this course needs a lesson, so add another lesson for ${lost.name} first.`,
          confirmLabel: 'OK',
          onConfirm: () => undefined,
          required: true
        });
        return null;
      }
      return (await retagLesson(mIdx, lIdx, choice)) ? capability : null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lessonTags, modules, structureModules, retagLesson]
  );

  // The lesson's Scored Roleplay flag, kept in step with its toggle (the backend already saved it).
  const setLessonScored = useCallback(
    (mIdx: number, lIdx: number, scored: boolean) => {
      const flag = <L extends { roleplay?: boolean }>(lessons: L[]) =>
        lessons.map((l, li) => (li === lIdx ? { ...l, roleplay: scored } : l));
      updateStructure({
        generatedStructure: {
          modules: structureModules.map((m, mi) => (mi === mIdx ? { ...m, lessons: flag(m.lessons) } : m))
        }
      });
      updateContent((prev: ContentData): ContentData => {
        const gen = prev.generatedContent as GenerationStatus | null;
        if (!gen?.modules) return prev;
        return {
          ...prev,
          generatedContent: { ...gen, modules: gen.modules.map((m, mi) => (mi === mIdx ? { ...m, lessons: flag(m.lessons) } : m)) }
        };
      });
    },
    [structureModules, updateStructure, updateContent]
  );

  const roleplayPolicyFor = (mIdx: number, lIdx: number): RoleplayPolicy | undefined => {
    const lesson = modules[mIdx]?.lessons[lIdx];
    if (!lessonTags || !lesson) return undefined;
    const at = { moduleIndex: mIdx, lessonIndex: lIdx };
    return {
      capability: lessonCapability(lesson, lessonTags),
      outcomes: lesson.outcomes ?? [],
      lastScored: Boolean(lastScoredRoleplayOf(modules, lessonTags, scoredFlags, at)),
      stillMeasuredIn: otherScoredRoleplayLesson(modules, scoredFlags, at)?.title ?? null,
      pickCapability: (reason) => pickCapability(mIdx, lIdx, reason),
      onScoredChange: (scored) => setLessonScored(mIdx, lIdx, scored)
    };
  };

  const isGeneratingContent = content?.status === 'generating' || isContentGenerating;
  const isContentCompleted = content?.status === 'completed';

  const [reasoningPanelOpen, setReasoningPanelOpen] = useState(false);
  const [moduleDueDateTargetIdx, setModuleDueDateTargetIdx] = useState<number | null>(null);
  const [moduleDueDates, setModuleDueDates] = useState<Record<number, DueDateData>>({});

  return (
    <Box sx={{ width: '100%', display: 'flex', gap: 3, minHeight: 0, flex: 1 }}>
      {/* Left panel */}
      <Box
        sx={{
          width: 320,
          flexShrink: 0,
          overflowY: 'auto',
          borderRight: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
          pr: 2
        }}
      >
        <ModuleSidebar
          programName={state.requirements.programName}
          modules={sidebarModules}
          selectedModuleIdx={selectedModuleIdx}
          selectedLessonIdx={selectedLessonIdx}
          onSelectModule={(idx) => {
            setSelectedModuleIdx(idx);
            setSelectedLessonIdx(null);
          }}
          onSelectLesson={(mIdx, lIdx) => {
            setSelectedModuleIdx(mIdx);
            setSelectedLessonIdx(lIdx);
          }}
          onReorder={handleReorder}
          onRenameModule={handleRenameModule}
          onRenameLesson={handleRenameLesson}
          onAddModule={() => {
          handleAdd(null);
        }}
          onAddLesson={(mIdx) => {
          handleAdd(mIdx);
        }}
          onDeleteModule={handleDeleteModule}
          onDeleteLesson={handleDeleteLesson}
          renderLessonExtra={
            lessonTags
              ? (mIdx, lIdx) =>
                  modules[mIdx]?.lessons[lIdx] ? (
                    // What the lesson is for; it changes from the lesson's Roleplay section (Change).
                    <LessonTagLine
                      lesson={modules[mIdx].lessons[lIdx]}
                      roleplay={Boolean(scoredFlags[mIdx]?.[lIdx])}
                      tags={lessonTags}
                      editable={false}
                      onTag={() => undefined}
                    />
                  ) : null
              : undefined
          }
          onReasoningClick={(mIdx) => {
            setSelectedModuleIdx(mIdx);
            setSelectedLessonIdx(null);
          }}
          onImproveModuleClick={
            isContentCompleted
              ? (mIdx) => {
                  setSelectedModuleIdx(mIdx);
                  setImproveModuleOpen(true);
                }
              : undefined
          }
          onModuleDueDate={isContentCompleted ? (mIdx) => setModuleDueDateTargetIdx(mIdx) : undefined}
          readOnly={!isContentCompleted}
          hideProgramName={embedded}
          generatedModules={modules}
          pendingMediaLessons={
            new Set([
              ...Array.from(pendingImageLessons),
              ...(videoPollingJobs || []).map((j) => `${j.moduleIdx}-${j.lessonIdx}`)
            ])
          }
        />
      </Box>

      {/* Right panel */}
      <Box
        sx={{ flex: 1, overflowY: 'auto', pt: embedded ? '16px' : 1, pb: 1, display: 'flex', flexDirection: 'column' }}
      >
        {/* Show generating overlay in the right panel while content is being generated */}
        {isGeneratingContent && !selectedLesson ? (
          <GeneratingOverlay progress={generationProgress} />
        ) : selectedLesson ? (
          <LessonContentView
            headerAction={<StepInviteAdmin />}
            roleplayPolicy={roleplayPolicyFor(selectedModuleIdx!, selectedLessonIdx!)}
            lesson={selectedLesson}
            moduleName={modules[selectedModuleIdx!]?.title || sidebarModules[selectedModuleIdx!]?.title || ''}
            moduleIndex={selectedModuleIdx!}
            lessonIndex={selectedLessonIdx!}
            generationId={ctx.state.generationId}
            onEdit={() => setEditLessonOpen(true)}
            onImprove={() => setImproveLessonOpen(true)}
            onReasoningClick={() => setReasoningPanelOpen(true)}
            onMaterialUpdate={handleMaterialUpdate}
            onAssessmentsUpdate={handleAssessmentsUpdate}
            onAddMaterial={() => {
              setAddMaterialAction('Add');
              setAddMaterialOpen(true);
            }}
            onRemoveMaterial={handleRemoveMaterial}
            onGenerateImage={
              ctx.state.generationId
                ? () => {
                    setMediaDialog({ type: 'image', open: true });
                    setMediaPrompt('');
                  }
                : undefined
            }
            onGenerateVideo={
              ctx.state.generationId
                ? () => {
                    setMediaDialog({ type: 'video', open: true });
                    setMediaPrompt('');
                  }
                : undefined
            }
            onRemoveMedia={(src, type) => handleRemoveMedia(src, type, selectedModuleIdx!, selectedLessonIdx!)}
            imageCount={mediaImageCount}
            videoCount={mediaVideoCount}
            isGeneratingImage={pendingImageLessons.has(`${selectedModuleIdx}-${selectedLessonIdx}`)}
            isGeneratingVideo={(videoPollingJobs || []).some(
              (j) => j.moduleIdx === selectedModuleIdx && j.lessonIdx === selectedLessonIdx
            )}
          />
        ) : selectedModuleIdx !== null && modules[selectedModuleIdx] ? (
          /* Module overview */
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1 }}>
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 600,
                  fontFamily: 'Inter',
                  color: 'var(--Text-text-primary, #FDFDFD)'
                }}
              >
                {modules[selectedModuleIdx].title}
              </Typography>
              <StepInviteAdmin />
            </Box>
            <Box sx={{ maxWidth: 560 }}>
            <Typography
              sx={{
                fontSize: 14,
                fontFamily: 'Inter',
                color: 'var(--Text-text-secondary, #B6B6B6)',
                lineHeight: '22px',
                mb: 2
              }}
            >
              {modules[selectedModuleIdx].reasoning}
            </Typography>
            <Typography sx={{ fontSize: 13, fontFamily: 'Inter', color: 'var(--Text-text-tertiary, #868686)' }}>
              {modules[selectedModuleIdx].completedLessonsCount}/{modules[selectedModuleIdx].totalLessonsCount} lessons
              completed
            </Typography>
            </Box>
          </Box>
        ) : (
          /* No selection */
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <Typography
                sx={{
                  fontSize: 24,
                  fontWeight: 600,
                  fontFamily: 'Inter',
                  color: 'var(--Text-text-primary, #FDFDFD)'
                }}
              >
                Lessons
              </Typography>
              <StepInviteAdmin />
            </Box>
            <Box>
            <Typography
              sx={{
                fontSize: 14,
                fontFamily: 'Inter',
                color: 'var(--Text-text-secondary, #B6B6B6)',
                lineHeight: '22px',
                whiteSpace: 'nowrap'
              }}
            >
              Select a lesson from the sidebar to view and edit its generated content, materials, and assessments.
            </Typography>
            {generationError && (
              <Typography sx={{ fontSize: 14, fontFamily: 'Inter', color: 'var(--UI-Error, #F44336)', mt: 2 }}>
                {generationError}
              </Typography>
            )}
            </Box>
          </Box>
        )}
      </Box>

      {/* Modals */}
      {selectedLesson && (
        <EditLessonModal
          open={editLessonOpen}
          onClose={() => setEditLessonOpen(false)}
          onSave={handleEditSave}
          lessonTitle={selectedLesson.title}
          lessonContent={selectedLesson.content}
        />
      )}

      <ImproveWithAIModal
        variant="lesson"
        open={improveLessonOpen}
        onClose={() => setImproveLessonOpen(false)}
        onSubmit={handleImproveLessonSubmit}
        isLoading={false}
        entityName={selectedLesson?.title}
        showVideoNotice={hasLessonVideo(selectedLesson?.content)}
      />

      <Dialog
        open={improvingLesson}
        PaperProps={{
          sx: {
            background: '#1A1A1A',
            borderRadius: '12px',
            border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
            p: 3,
            minWidth: 360
          }
        }}
      >
        <Typography
          sx={{ fontSize: 22, fontWeight: 700, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)', mb: 2 }}
        >
          Making changes
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HourglassBottomIcon
            sx={{
              fontSize: 18,
              color: 'var(--Text-text-secondary, #B6B6B6)',
              animation: 'hourglass-spin 2s ease-in-out infinite',
              '@keyframes hourglass-spin': {
                '0%, 40%': { transform: 'rotate(0deg)' },
                '60%, 100%': { transform: 'rotate(180deg)' }
              }
            }}
          />
          <Typography sx={{ fontSize: 14, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)' }}>
            Please wait as we improve the program content...
          </Typography>
        </Box>
      </Dialog>

      <ImproveWithAIModal
        variant="module"
        open={improveModuleOpen}
        onClose={() => setImproveModuleOpen(false)}
        onSubmit={handleImproveModuleSubmit}
        isLoading={false}
        entityName={selectedModuleIdx !== null ? modules[selectedModuleIdx]?.title : undefined}
      />

      <ImproveWithAIModal
        variant="program"
        open={improveProgramOpen}
        onClose={() => setImproveProgramOpen(false)}
        onSubmit={handleImproveProgramSubmit}
        isLoading={false}
      />

      {/* AI Reasoning modal for selected lesson */}
      <Dialog
        open={reasoningPanelOpen && !!selectedLesson}
        onClose={() => setReasoningPanelOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: '#1A1A1A',
            borderRadius: '12px',
            border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
            p: 3,
            maxHeight: '80vh'
          }
        }}
      >
        {selectedLesson &&
          (() => {
            const allDocs = [...state.documents.uploadedDocuments, ...state.documents.previousDocuments];
            const findDoc = (title: string) => allDocs.find((d) => d.title === title);
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 600,
                          fontFamily: 'Inter',
                          color: 'var(--Stroke-highlight, #F79420)'
                        }}
                      >
                        Elliott's reasoning
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 22,
                        fontWeight: 700,
                        fontFamily: 'Inter',
                        color: 'var(--Text-text-primary, #FDFDFD)'
                      }}
                    >
                      {selectedLesson.title}
                    </Typography>
                  </Box>
                  <Box
                    component="button"
                    onClick={() => setReasoningPanelOpen(false)}
                    sx={{ background: 'none', border: 'none', cursor: 'pointer', p: 0.5, flexShrink: 0 }}
                  >
                    <CloseIcon sx={{ color: 'var(--Text-text-secondary, #B6B6B6)', fontSize: 24 }} />
                  </Box>
                </Box>

                {selectedLesson.reasoning && (
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontFamily: 'Inter',
                      color: 'var(--Text-text-secondary, #B6B6B6)',
                      lineHeight: '24px'
                    }}
                  >
                    {selectedLesson.reasoning}
                  </Typography>
                )}

                {(selectedLesson.learningOutcomesServed?.length ?? 0) > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        fontFamily: 'Inter',
                        color: 'var(--Text-text-primary, #FDFDFD)',
                        mb: 1.5
                      }}
                    >
                      Serving goals:
                    </Typography>
                    {selectedLesson.learningOutcomesServed?.map((lo, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontFamily: 'Inter',
                            color: 'var(--Text-text-secondary, #B6B6B6)',
                            lineHeight: '20px'
                          }}
                        >
                          {lo}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {(selectedLesson.ragDocumentTitles?.length ?? 0) > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        fontFamily: 'Inter',
                        color: 'var(--Text-text-primary, #FDFDFD)',
                        mb: 1.5
                      }}
                    >
                      Documents informing:
                    </Typography>
                    {selectedLesson.ragDocumentTitles?.map((title, i) => {
                      const doc = findDoc(title);
                      return (
                        <Box key={i} sx={{ display: 'flex', flexDirection: 'column', mb: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DescriptionIcon sx={{ fontSize: 16, color: 'var(--Text-text-tertiary, #868686)' }} />
                            <Typography
                              sx={{
                                fontSize: 14,
                                fontWeight: 500,
                                fontFamily: 'Inter',
                                color: 'var(--Text-text-secondary, #B6B6B6)'
                              }}
                            >
                              {title}
                            </Typography>
                            {doc?.fileTypeLabel && (
                              <Box
                                sx={{
                                  px: 0.75,
                                  py: 0.25,
                                  borderRadius: '4px',
                                  background: 'rgba(255,255,255,0.1)',
                                  fontSize: 10,
                                  fontWeight: 700,
                                  fontFamily: 'Inter',
                                  color: 'var(--Text-text-secondary, #B6B6B6)',
                                  lineHeight: '14px'
                                }}
                              >
                                {doc.fileTypeLabel}
                              </Box>
                            )}
                          </Box>
                          {doc?.instructions && (
                            <Typography
                              sx={{
                                fontSize: 12,
                                fontFamily: 'Inter',
                                color: 'var(--Text-text-tertiary, #868686)',
                                ml: 3.5,
                                mt: 0.25
                              }}
                            >
                              Instructions: {doc.instructions}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            );
          })()}
      </Dialog>

      <DueDateModal
        open={moduleDueDateTargetIdx !== null}
        onClose={() => setModuleDueDateTargetIdx(null)}
        mode="module"
        initialData={
          moduleDueDateTargetIdx !== null
            ? {
                ...moduleDueDates[moduleDueDateTargetIdx],
                description:
                  moduleDueDates[moduleDueDateTargetIdx]?.description ??
                  modules[moduleDueDateTargetIdx]?.reasoning ??
                  sidebarModules[moduleDueDateTargetIdx]?.reasoning
              }
            : undefined
        }
        onSave={(data) => {
          if (moduleDueDateTargetIdx !== null) {
            setModuleDueDates((prev) => ({ ...prev, [moduleDueDateTargetIdx]: data }));
          }
          setModuleDueDateTargetIdx(null);
        }}
      />

      {/* Add New Material */}
      {/* Media generation prompt dialog */}
      <Dialog
        open={mediaDialog.open}
        onClose={() => setMediaDialog({ type: 'image', open: false })}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { background: '#1A1A1A', borderRadius: '12px', border: '1px solid #2A2A2A', p: 3 } }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 600, fontFamily: 'Inter', color: '#FDFDFD' }}>
            {mediaDialog.type === 'image' ? 'Generate Lesson Image' : 'Generate Lesson Video'}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setMediaDialog({ type: 'image', open: false })}
            sx={{ color: '#B6B6B6' }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* Description */}
        <Typography sx={{ fontSize: 13, fontFamily: 'Inter', color: '#B6B6B6', mb: 2 }}>
          {mediaDialog.type === 'image'
            ? 'Describe the image you want to generate for this lesson.'
            : 'These instructions will be sent to Elliott, who will provide a video explanation for this lesson based on its content and your instructions. Describe what Elliott should consider in this lesson video.'}
        </Typography>

        {/* Elliott image (video only) — above the text box */}
        {mediaDialog.type === 'video' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Box
              component="img"
              src={require('../../../../../assets/images/elliott-avatar.png.webp')}
              alt="Elliott"
              sx={{ width: 160, borderRadius: '12px', display: 'block' }}
            />
          </Box>
        )}

        <TextField
          fullWidth
          multiline
          minRows={3}
          value={mediaPrompt}
          onChange={(e) => setMediaPrompt(e.target.value)}
          placeholder={
            mediaDialog.type === 'image'
              ? 'e.g. A diagram showing customer journey stages'
              : 'e.g. Focus on practical examples and keep it under 2 minutes'
          }
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': { color: '#FDFDFD', background: '#0F0F0F', borderRadius: '8px' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2A2A2A' },
            '& textarea::placeholder': { color: '#868686' }
          }}
        />

        <Box display="flex" justifyContent="center">
          <DosenButton tone="cta" sizeVariant="lg" disabled={!mediaPrompt.trim()} onClick={handleGenerateMedia}>
            Generate
          </DosenButton>
        </Box>
      </Dialog>

      <AddEditContent
        openForm={addMaterialOpen}
        handleClickCloseForm={() => setAddMaterialOpen(false)}
        setOpenForm={setAddMaterialOpen}
        selectedActiveId=""
        singleListData={{}}
        action={addMaterialAction}
        setAction={setAddMaterialAction}
        onNewArticleCreated={handleNewArticleCreated}
        newGroupId={ctx.groupId}
      />

      <CapabilityConfirm request={confirm} onClose={() => setConfirm(null)} />
      <LessonPurposeDialog
        open={Boolean(purpose)}
        title={purpose?.title ?? ''}
        body={purpose?.body}
        options={purpose?.options ?? []}
        initial={purpose?.initial}
        titleField={purpose?.titleField}
        confirmLabel={purpose?.confirmLabel ?? ''}
        onCancel={() => closePurpose(null)}
        onConfirm={(choice, title) => closePurpose({ choice, title })}
      />
    </Box>
  );
};

export default ContentStep;
